/**
 * PullToRefresh - Pull to refresh wrapper component
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

export const PullToRefresh = ({ children, onRefresh, className = "" }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const isPulling = useRef(false);
  
  const threshold = 80;
  const resistance = 2.5;

  // Check if we're at the top of the scroll area
  const isAtTop = useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;
    
    // Check both container and window scroll position
    const containerScrollTop = container.scrollTop;
    const windowScrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    
    return containerScrollTop <= 0 && windowScrollTop <= 0;
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (isAtTop()) {
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, [isAtTop]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current || isRefreshing) return;
    
    if (!isAtTop()) {
      isPulling.current = false;
      setPullDistance(0);
      return;
    }
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    
    if (diff > 0) {
      // Prevent default scroll when pulling
      e.preventDefault();
      const distance = Math.min(diff / resistance, threshold * 1.5);
      setPullDistance(distance);
    }
  }, [isRefreshing, isAtTop]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    
    if (pullDistance >= threshold && !isRefreshing && onRefresh) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setPullDistance(0);
  }, [pullDistance, isRefreshing, onRefresh]);

  // Handle passive event listener for touch events that need preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const touchMoveHandler = (e) => {
      if (isPulling.current && pullDistance > 0) {
        e.preventDefault();
      }
    };
    
    container.addEventListener('touchmove', touchMoveHandler, { passive: false });
    return () => container.removeEventListener('touchmove', touchMoveHandler);
  }, [pullDistance]);

  const pullProgress = Math.min(pullDistance / threshold, 1);
  const shouldRefresh = pullDistance >= threshold;

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-auto ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* Pull indicator */}
      <AnimatePresence>
        {(pullDistance > 10 || isRefreshing) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ 
              opacity: 1, 
              height: isRefreshing ? 60 : Math.max(pullDistance, 0)
            }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center justify-center bg-gradient-to-b from-primary/5 to-transparent overflow-hidden"
          >
            <motion.div
              animate={{ 
                rotate: isRefreshing ? 360 : pullProgress * 180,
                scale: shouldRefresh || isRefreshing ? 1 : 0.8
              }}
              transition={{ 
                rotate: isRefreshing ? { duration: 1, repeat: Infinity, ease: "linear" } : { duration: 0 },
                scale: { duration: 0.2 }
              }}
              className={`p-2 rounded-full ${
                shouldRefresh || isRefreshing 
                  ? 'bg-primary text-white' 
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <RefreshCw className="w-5 h-5" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Content */}
      <div style={{ 
        transform: `translateY(${isRefreshing ? 0 : 0}px)`,
        transition: 'transform 0.2s ease-out'
      }}>
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
